<template>
  <v-card width="428">
    <v-card-title class="primary--text pa-4">
      Add profile
    </v-card-title>

    <v-select
      :menu-props="{ bottom: true, offsetY: true }"
      data-test-input="application"
      v-model="schema"
      :items="schemas"
      item-text="name"
      item-value="id"
      class="px-4"
      label="Application"
      return-object
    >
      <template v-slot:item="{ item }">
        <span :data-test-select="item.name">{{ item.name }}</span>
      </template>
    </v-select>
    <v-select
      :menu-props="{ bottom: true, offsetY: true }"
      data-test-input="userGroup"
      v-model="group"
      :items="groupItems"
      item-text="groupName"
      item-value="id"
      class="px-4"
      label="User group"
    >
      <template v-slot:item="{ item }">
        <span :data-test-select-group="item.groupName">{{
          item.groupName
        }}</span>
      </template>
    </v-select>
    <v-card-text class="body-2 pa-4 pt-0">
      You need to set up profile using rights so that the user can save their
      personal application settings
    </v-card-text>
    <form-subheader class="px-4">
      Notification deliveries
    </form-subheader>
    <v-list class="pa-0 pb-4">
      <v-list-item
        dense
        :key="item.key"
        v-for="item in availableNotificationProfiles"
      >
        <v-checkbox
          v-model="notificationProfiles"
          :hide-details="true"
          :value="item.key"
          :label="item.title"
          color="primary"
          :ripple="false"
        ></v-checkbox>
      </v-list-item>
    </v-list>

    <v-divider />
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="close">Cancel</v-btn>
      <v-btn
        data-test="addProfile"
        text
        color="primary"
        @click="saveHandler"
        :loading="loading"
        >Add
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  computed,
  defineComponent,
  onMounted,
  ref
} from '@vue/composition-api';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import schemasGql from '@/api/schemas/list.graphql';
import gql from '@/api/objects/item.graphql';
import cloneDeep from 'lodash.clonedeep';

export default defineComponent({
  props: {
    userName: {
      type: String
    },
    user: {
      type: Object,
      default: null
    },
    type: {
      type: String
    }
  },
  setup(props, { root, emit }) {
    const notificationProfiles = ref(['In-app']);
    const { mutate: linkObjToObj } = useMutation(gql.connectObjectsToObject);

    const { mutate: createObject } = useMutation(
      gql.createObjectWithProperties
    );

    const filter = {
      mTags: {
        contains: []
      }
    };

    if (props.type === 'USER') {
      filter.mTags.contains = ['application', 'user profile'];
    } else {
      filter.mTags.contains = ['application', 'app profile'];
    }

    const { result } = useQuery(
      schemasGql.loadPure,
      {
        filter
      },
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );

    const { result: notificationProfile, onResult } = useQuery(
      schemasGql.loadPureWithProperties,
      {
        filter: {
          mTags: {
            contains: ['application', 'notification profile']
          }
        }
      },
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );

    const groupItems = computed(() => {
      if (props.user) {
        return props.user.usersToGroups.map(item => item.userGroup);
      }
      return [];
    });

    const schemas = useResult(result, [], data => {
      return data?.schemata;
    });

    const schemaNotificationProfile = computed(
      () => notificationProfile.value?.schemata?.[0]
    );

    const availableNotificationProfiles = computed(
      () =>
        notificationProfile.value?.schemata?.[0]?.schemaProperties?.find(
          item => item.key === 'settingsDeliveryChannel'
        )?.valueSet?.list || []
    );

    const schema = ref(null);
    const group = ref(null);

    const close = () => {
      emit('close');
    };

    const saveHandler = () => {
      let profileId = null;
      const getName = arr => {
        let str = arr
          .join(' ')
          .split(' ')
          .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`);
        return `${str.join(' ')} (${props.userName})`;
      };

      const payload = {
        enabled: true,
        schemaId: schema.value.id,
        name: getName(schema.value.mTags),
        userProfile: root.$route.params.id
      };

      if (group.value) {
        payload.userGroup = group.value;
      }

      createObject({
        input: payload
      })
        .then(res => {
          profileId = res.data.createObjectWithProperties.uuid;

          const notificationProfilePromises = [];

          notificationProfiles.value.forEach(item => {
            const name = `${schema.value.mTags[1]
              .charAt(0)
              .toUpperCase()}${schema.value.mTags[1].slice(1)}`;
            notificationProfilePromises.push(
              createObject({
                input: {
                  enabled: true,
                  schemaId: schemaNotificationProfile.value.id,
                  name: `${name} Notification Profile (${item})`,
                  keyedProperties: [
                    {
                      propertyKey: 'settingsDeliveryChannel',
                      value: item
                    }
                  ]
                }
              })
            );
          });

          return Promise.all(notificationProfilePromises);
        })
        .then(res => {
          const linkPromises = [];

          res.forEach(item => {
            linkPromises.push(
              linkObjToObj({
                input: {
                  objectsToObject: {
                    object1Id: profileId,
                    object2Id: item.data.createObjectWithProperties.uuid
                  }
                }
              })
            );
          });

          return Promise.all(linkPromises);
        })
        .then(() => {
          close();
        });
    };

    const handleClose = data => {
      const indexDeleting = notificationProfiles.value.findIndex(
        item => item.key === data.key
      );
      notificationProfiles.value.splice(indexDeleting, 1);
    };

    return {
      handleClose,
      saveHandler,
      close,
      schema,
      schemas,
      groupItems,
      group,
      loading: false,
      schemaNotificationProfile,
      availableNotificationProfiles,
      notificationProfiles
    };
  }
});
</script>
